// Client-side validation utilities matching backend validators

const DOMAIN_REGEX = /^([a-z0-9]+(-[a-z0-9]+)*\.)+[a-z]{2,}$/i;

const MIN_KEYWORD_LENGTH = 1;
const MAX_KEYWORD_LENGTH = 200;
const MIN_KEYWORDS_COUNT = 1;
const MAX_KEYWORDS_COUNT = 1000;

// Geo target and language allowed values are enforced server-side against the
// database referential. The front only offers values fetched from the API, so
// there is no static list to validate against here.
const ALLOWED_DEVICE_TARGETS = new Set(['mobile', 'desktop', 'tablet']);

export const buildKeywordDedupKey = (entry) => {
    const keyword = (entry.keyword ?? "").trim().toLowerCase();
    const category = (entry.category ?? "").trim().toLowerCase();
    const location = (entry.location ?? "").trim().toLowerCase();
    return `${keyword}|${category}|${location}`;
};

export const getUniqueKeywordEntryCount = (entries) => {
    if (!entries || entries.length === 0) return 0;

    const seen = new Set();
    entries.forEach(entry => {
        const key = buildKeywordDedupKey(entry);
        seen.add(key);
    });
    return seen.size;
};

const deduplicateKeywordEntries = (entries, result) => {
    const originalCount = entries.length;
    const seen = new Set();
    const uniqueEntries = [];

    entries.forEach(entry => {
        const key = buildKeywordDedupKey(entry);
        if (!seen.has(key)) {
            seen.add(key);
            uniqueEntries.push(entry);
        }
    });

    const duplicateCount = originalCount - uniqueEntries.length;

    if (duplicateCount > 0) {
        result.addWarning(
            `${duplicateCount} doublon(s) détecté(s) et automatiquement supprimé(s) (${uniqueEntries.length} mots-clés uniques conservés)`
        );
    }

    return uniqueEntries;
};

export class ValidationResult {
    constructor() {
        this.isValid = true;
        this.errors = [];
        this.warnings = [];
        this.cleanedKeywords = null;
    }

    addError(error) {
        this.isValid = false;
        this.errors.push(error);
    }

    addWarning(warning) {
        this.warnings.push(warning);
    }

    static success() {
        return new ValidationResult();
    }

    static failure(error) {
        const result = new ValidationResult();
        result.addError(error);
        return result;
    }
}

export const validateLaunchStudyRequest = (clientDomain, keywordEntries, geoTarget, language, deviceTarget) => {
    const result = new ValidationResult();

    // Validate client domain
    if (!clientDomain || clientDomain.trim() === '') {
        result.addError('Le domaine client est requis');
    } else if (!DOMAIN_REGEX.test(clientDomain.trim())) {
        result.addError(`Format de domaine invalide: '${clientDomain}'. Format attendu: 'example.com'`);
    }

    // Validate keywords
    if (!keywordEntries || keywordEntries.length === 0) {
        result.addError('Au moins un mot-clé est requis');
    } else {
        if (keywordEntries.length < MIN_KEYWORDS_COUNT) {
            result.addError(`Minimum ${MIN_KEYWORDS_COUNT} mot-clé requis`);
        }

        const cleanedKeywords = deduplicateKeywordEntries(keywordEntries, result);

        if (cleanedKeywords.length > MAX_KEYWORDS_COUNT) {
            result.addError(`Limite dépassée : ${cleanedKeywords.length} mots-clés après suppression des doublons (maximum : ${MAX_KEYWORDS_COUNT})`);
        }

        cleanedKeywords.forEach((entry, index) => {
            const keyword = entry.keyword;
            if (!keyword || keyword.trim() === '') {
                result.addError(`Le mot-clé à l'index ${index} est vide`);
                return;
            }

            const trimmedKeyword = keyword.trim();

            if (trimmedKeyword.length < MIN_KEYWORD_LENGTH) {
                result.addError(`Le mot-clé '${trimmedKeyword}' est trop court (minimum ${MIN_KEYWORD_LENGTH} caractère)`);
            }

            if (trimmedKeyword.length > MAX_KEYWORD_LENGTH) {
                result.addError(`Le mot-clé '${trimmedKeyword}' est trop long (maximum ${MAX_KEYWORD_LENGTH} caractères)`);
            }
        });

        result.cleanedKeywords = cleanedKeywords;
    }

    // Geo target and language are validated server-side against the referential.

    // Validate device target
    if (deviceTarget && !ALLOWED_DEVICE_TARGETS.has(deviceTarget.toLowerCase())) {
        result.addError(`Cible d'appareil invalide: '${deviceTarget}'. Valeurs autorisées: ${Array.from(ALLOWED_DEVICE_TARGETS).join(', ')}`);
    }

    return result;
};

// Individual validation functions for real-time feedback
export const validateDomain = (domain) => {
    if (!domain || domain.trim() === '') {
        return { isValid: false, error: 'Le domaine est requis' };
    }
    
    if (!DOMAIN_REGEX.test(domain.trim())) {
        return { isValid: false, error: 'Format de domaine invalide (ex: example.com)' };
    }
    
    return { isValid: true };
};

export const validateKeywordCount = (keywords) => {
    if (!keywords || keywords.length === 0) {
        return { isValid: false, error: 'Au moins un mot-clé est requis' };
    }
    
    if (keywords.length > MAX_KEYWORDS_COUNT) {
        return { isValid: false, error: `Maximum ${MAX_KEYWORDS_COUNT} mots-clés autorisés` };
    }
    
    return { isValid: true };
};